import { useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useFormikContext } from "formik";
import type { PatientRecord } from "./types";

type OrderFormValues = Record<string, unknown> & {
  patientUpdateStatus?: string;
  patientProperties?: PatientRecord;
};

interface PatientFormObserverProps {
  setOrderFormValues: Dispatch<SetStateAction<OrderFormValues>>;
  /**
   * Writes the form into the order without counting it as an edit. Used when
   * the form only restates the patient the order already holds (NO_ACTION),
   * e.g. once the order's patient is saved and the form reopens on it; the
   * host's ordinary setter would otherwise flag "Unsaved changes" (OGC-1443).
   */
  hydrateOrderFormValues?: Dispatch<SetStateAction<OrderFormValues>>;
  formAction: string;
  selectedPatient?: PatientRecord;
}

const normalizeAddressHierarchy = (patient: PatientRecord = {}) => {
  const normalized: Record<string, string> = {};

  Object.entries(patient.addressHierarchy || {}).forEach(([key, value]) => {
    normalized[key] = value || "";
  });

  Object.keys(patient)
    .filter((key) => key.startsWith("addressHierarchy_"))
    .sort()
    .forEach((key) => {
      normalized[key] = patient[key] || "";
    });

  return normalized;
};

const normalizePatientForComparison = (patient = {}) => ({
  patientPK: patient.patientPK || "",
  STnumber: patient.STnumber || "",
  subjectNumber: patient.subjectNumber || "",
  nationalId: patient.nationalId || "",
  guid: patient.guid || "",
  lastName: patient.lastName || "",
  firstName: patient.firstName || "",
  aka: patient.aka || "",
  mothersName: patient.mothersName || "",
  mothersInitial: patient.mothersInitial || "",
  streetAddress: patient.streetAddress || "",
  city: patient.city || "",
  commune: patient.commune || "",
  addressDepartment: patient.addressDepartment || "",
  gender: patient.gender || "",
  birthDateForDisplay: patient.birthDateForDisplay || "",
  patientType: patient.patientType || "",
  insuranceNumber: patient.insuranceNumber || "",
  occupation: patient.occupation || "",
  primaryPhone: patient.primaryPhone || "",
  email: patient.email || "",
  healthRegion: patient.healthRegion || "",
  education: patient.education || "",
  maritialStatus: patient.maritialStatus || "",
  nationality: patient.nationality || "",
  healthDistrict: patient.healthDistrict || "",
  otherNationality: patient.otherNationality || "",
  patientContact: {
    id: patient.patientContact?.id || "",
    person: {
      firstName: patient.patientContact?.person?.firstName || "",
      lastName: patient.patientContact?.person?.lastName || "",
      primaryPhone: patient.patientContact?.person?.primaryPhone || "",
      email: patient.patientContact?.person?.email || "",
    },
  },
  addressHierarchy: normalizeAddressHierarchy(patient),
});

/**
 * Writes the patient form into the order. A patient the order already holds
 * (saved with the order, or chosen from the search) is never turned back into
 * a new one: the order keeps its patientPK, and a form that would add the
 * patient again updates it instead.
 */
export const mergePatientIntoOrderFormValues = (
  orderFormValues: OrderFormValues = {},
  values: PatientRecord = {},
  patientUpdateStatus: string,
) => {
  const heldPatientPK = orderFormValues.patientProperties?.patientPK || "";
  const keepsHeldPatient = !values.patientPK && heldPatientPK;
  const nextStatus =
    keepsHeldPatient && patientUpdateStatus === "ADD"
      ? "UPDATE"
      : patientUpdateStatus;
  const nextPatientProperties = {
    ...values,
    ...(keepsHeldPatient ? { patientPK: heldPatientPK } : {}),
    patientUpdateStatus: nextStatus,
  };

  if (
    orderFormValues.patientUpdateStatus === nextStatus &&
    JSON.stringify(orderFormValues.patientProperties || {}) ===
      JSON.stringify(nextPatientProperties)
  ) {
    return orderFormValues;
  }

  return {
    ...orderFormValues,
    patientUpdateStatus: nextStatus,
    patientProperties: nextPatientProperties,
  };
};

export const derivePatientUpdateStatus = (
  values: PatientRecord,
  selectedPatient: PatientRecord | undefined,
  fallbackAction: string,
) => {
  if (!selectedPatient?.patientPK) {
    return fallbackAction;
  }

  return JSON.stringify(normalizePatientForComparison(values)) ===
    JSON.stringify(normalizePatientForComparison(selectedPatient))
    ? "NO_ACTION"
    : "UPDATE";
};

const PatientFormObserver = (props: PatientFormObserverProps) => {
  const { values } = useFormikContext<PatientRecord>();
  const {
    setOrderFormValues,
    hydrateOrderFormValues,
    formAction,
    selectedPatient,
  } = props;

  useEffect(() => {
    const patientUpdateStatus = derivePatientUpdateStatus(
      values,
      selectedPatient,
      formAction,
    );
    const write =
      patientUpdateStatus === "NO_ACTION" && hydrateOrderFormValues
        ? hydrateOrderFormValues
        : setOrderFormValues;

    write((previousOrderFormValues) =>
      mergePatientIntoOrderFormValues(
        previousOrderFormValues,
        values,
        patientUpdateStatus,
      ),
    );
  }, [
    formAction,
    hydrateOrderFormValues,
    selectedPatient,
    setOrderFormValues,
    values,
  ]);

  return null;
};

export default PatientFormObserver;
